import React, { useState } from 'react';
import { Plus, DollarSign, TrendingUp, Clock, CheckCircle, Download, Bell, AlertCircle, XCircle, Eye, Send } from 'lucide-react';
import Sidebar, { useSidebar } from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';

import TopNavbar from '../../components/ui/TopNavbar';
import Icon from '../../components/AppIcon';
import InvoiceTemplate from './components/InvoiceTemplate';
import GenerateInvoiceModal from './components/GenerateInvoiceModal';



const AccountsReceivable = () => {
  const { isCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState('receivables');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isInvoiceViewOpen, setIsInvoiceViewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] = useState(false);
  
  // Exchange rates
  const USD_TO_INR = 83.50;
  const USD_TO_AUD = 1.52; // 1 USD = 1.52 AUD (approximate rate)
  
  // Enhanced receivables with comprehensive coal trading management fee data
  const [receivables, setReceivables] = useState([
    {
      id: 'AR-2024-001',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-125',
      description: 'Coal Trading Management Services - December 2024',
      invoiceDate: '2024-12-15',
      dueDate: '2025-01-15',
      tonnage: 320000,
      feeRate: 3.5,
      amountUSD: 1120000.00,
      amountAUD: 1702400.00,
      amountINR: 93520000.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'pending',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      invoiceMonth: '2024-12',
      generatedAt: '2024-12-15T10:30:00Z',
      vesselName: 'Pacific Glory',
      origin: 'Newcastle, Australia',
      destination: 'Mundra Port, India',
      coalType: 'Thermal Coal - 5500 GAR'
    },
    {
      id: 'AR-2024-002',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-118',
      description: 'Coal Trading Management Services - November 2024',
      invoiceDate: '2024-12-01',
      dueDate: '2025-01-01',
      tonnage: 150000,
      feeRate: 3.5,
      amountUSD: 525000.00,
      amountAUD: 798000.00,
      amountINR: 43837500.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2024-12-30',
      paidAmount: 525000.00,
      invoiceMonth: '2024-11',
      generatedAt: '2024-12-01T09:15:00Z',
      vesselName: 'Ocean Harmony',
      origin: 'Gladstone, Australia',
      destination: 'Vizag Port, India',
      coalType: 'Thermal Coal - 6000 GAR'
    },
    {
      id: 'AR-2025-001',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2025-001',
      description: 'Coal Trading Management Services - January 2025',
      invoiceDate: '2025-01-15',
      dueDate: '2025-02-15',
      tonnage: 380000,
      feeRate: 3.5,
      amountUSD: 1330000.00,
      amountAUD: 2021600.00,
      amountINR: 111005000.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2025-02-10',
      paidAmount: 1330000.00,
      invoiceMonth: '2025-01',
      generatedAt: '2025-01-15T10:30:00Z',
      vesselName: 'Star Navigator',
      origin: 'Newcastle, Australia',
      destination: 'Kandla Port, India',
      coalType: 'Thermal Coal - 5800 GAR'
    },
    {
      id: 'AR-2025-002',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2025-015',
      description: 'Coal Trading Management Services - February 2025',
      invoiceDate: '2025-02-15',
      dueDate: '2025-03-15',
      tonnage: 265000,
      feeRate: 3.5,
      amountUSD: 927500.00,
      amountAUD: 1409800.00,
      amountINR: 77446250.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2025-03-10',
      paidAmount: 927500.00,
      invoiceMonth: '2025-02',
      generatedAt: '2025-02-15T10:30:00Z',
      vesselName: 'Maritime Express',
      origin: 'Dalrymple Bay, Australia',
      destination: 'Paradip Port, India',
      coalType: 'Thermal Coal - 5500 GAR'
    },
    {
      id: 'AR-2024-003',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-130',
      description: 'Coal Trading Management Services - December 2024',
      invoiceDate: '2024-12-20',
      dueDate: '2025-01-20',
      tonnage: 185000,
      feeRate: 3.5,
      amountUSD: 647500.00,
      amountAUD: 984200.00,
      amountINR: 54066250.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'pending',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      invoiceMonth: '2024-12',
      generatedAt: '2024-12-20T14:20:00Z',
      vesselName: 'Global Trader',
      origin: 'Hay Point, Australia',
      destination: 'Tuticorin Port, India',
      coalType: 'Thermal Coal - 6200 GAR'
    },
    {
      id: 'AR-2024-004',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-112',
      description: 'Coal Trading Management Services - November 2024',
      invoiceDate: '2024-11-15',
      dueDate: '2024-12-15',
      tonnage: 340000,
      feeRate: 3.5,
      amountUSD: 1190000.00,
      amountAUD: 1808800.00,
      amountINR: 99365000.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'overdue',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      invoiceMonth: '2024-11',
      generatedAt: '2024-11-15T11:45:00Z',
      vesselName: 'Horizon Voyager',
      origin: 'Brisbane, Australia',
      destination: 'Chennai Port, India',
      coalType: 'Thermal Coal - 5700 GAR'
    },
    {
      id: 'AR-2024-005',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-135',
      description: 'Coal Trading Management Services - November 2024',
      invoiceDate: '2024-11-25',
      dueDate: '2024-12-25',
      tonnage: 225000,
      feeRate: 3.5,
      amountUSD: 787500.00,
      amountAUD: 1197000.00,
      amountINR: 65756250.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2024-12-20',
      paidAmount: 787500.00,
      invoiceMonth: '2024-11',
      generatedAt: '2024-11-25T16:10:00Z',
      vesselName: 'Neptune Star',
      origin: 'Newcastle, Australia',
      destination: 'Haldia Port, India',
      coalType: 'Thermal Coal - 6000 GAR'
    },
    {
      id: 'AR-2024-006',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-140',
      description: 'Coal Trading Management Services - December 2024',
      invoiceDate: '2024-12-05',
      dueDate: '2025-01-05',
      tonnage: 420000,
      feeRate: 3.5,
      amountUSD: 1470000.00,
      amountAUD: 2234400.00,
      amountINR: 122745000.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'pending',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      invoiceMonth: '2024-12',
      generatedAt: '2024-12-05T08:30:00Z',
      vesselName: 'Coastal Pride',
      origin: 'Port Kembla, Australia',
      destination: 'Krishnapatnam Port, India',
      coalType: 'Thermal Coal - 5500 GAR'
    },
    {
      id: 'AR-2024-007',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-108',
      description: 'Coal Trading Management Services - October 2024',
      invoiceDate: '2024-11-05',
      dueDate: '2024-12-05',
      tonnage: 295000,
      feeRate: 3.5,
      amountUSD: 1032500.00,
      amountAUD: 1569400.00,
      amountINR: 86213750.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2024-12-02',
      paidAmount: 1032500.00,
      invoiceMonth: '2024-10',
      generatedAt: '2024-11-05T13:20:00Z',
      vesselName: 'Atlantic Voyager',
      origin: 'Gladstone, Australia',
      destination: 'Ennore Port, India',
      coalType: 'Thermal Coal - 6100 GAR'
    },
    {
      id: 'AR-2025-003',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2025-022',
      description: 'Coal Trading Management Services - March 2025',
      invoiceDate: '2025-03-15',
      dueDate: '2025-04-15',
      tonnage: 355000,
      feeRate: 3.5,
      amountUSD: 1242500.00,
      amountAUD: 1888600.00,
      amountINR: 103748750.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'pending',
      approvalStatus: 'pending',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      invoiceMonth: '2025-03',
      generatedAt: '2025-03-15T10:30:00Z',
      vesselName: 'Southern Cross',
      origin: 'Newcastle, Australia',
      destination: 'Mundra Port, India',
      coalType: 'Thermal Coal - 5900 GAR'
    },
    {
      id: 'AR-2024-008',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-095',
      description: 'Coal Trading Management Services - September 2024',
      invoiceDate: '2024-10-15',
      dueDate: '2024-11-15',
      tonnage: 410000,
      feeRate: 3.5,
      amountUSD: 1435000.00,
      amountAUD: 2181200.00,
      amountINR: 119822500.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2024-11-10',
      paidAmount: 1435000.00,
      invoiceMonth: '2024-09',
      generatedAt: '2024-10-15T11:45:00Z',
      vesselName: 'Pacific Monarch',
      origin: 'Dalrymple Bay, Australia',
      destination: 'Vizag Port, India',
      coalType: 'Thermal Coal - 5600 GAR'
    },
    {
      id: 'AR-2024-009',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-150',
      description: 'Coal Trading Management Services - December 2024',
      invoiceDate: '2024-12-28',
      dueDate: '2025-01-28',
      tonnage: 275000,
      feeRate: 3.5,
      amountUSD: 962500.00,
      amountAUD: 1463000.00,
      amountINR: 80368750.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'pending',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      invoiceMonth: '2024-12',
      generatedAt: '2024-12-28T15:10:00Z',
      vesselName: 'Eastern Star',
      origin: 'Hay Point, Australia',
      destination: 'Kandla Port, India',
      coalType: 'Thermal Coal - 6300 GAR'
    },
    {
      id: 'AR-2024-010',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-085',
      description: 'Coal Trading Management Services - August 2024',
      invoiceDate: '2024-09-15',
      dueDate: '2024-10-15',
      tonnage: 315000,
      feeRate: 3.5,
      amountUSD: 1102500.00,
      amountAUD: 1675800.00,
      amountINR: 92058750.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2024-10-10',
      paidAmount: 1102500.00,
      invoiceMonth: '2024-08',
      generatedAt: '2024-09-15T10:30:00Z',
      vesselName: 'Diamond Carrier',
      origin: 'Newcastle, Australia',
      destination: 'Mundra Port, India',
      coalType: 'Thermal Coal - 5500 GAR'
    },
    {
      id: 'AR-2024-011',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-072',
      description: 'Coal Trading Management Services - July 2024',
      invoiceDate: '2024-08-15',
      dueDate: '2024-09-15',
      tonnage: 285000,
      feeRate: 3.5,
      amountUSD: 997500.00,
      amountAUD: 1516200.00,
      amountINR: 83291250.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2024-09-08',
      paidAmount: 997500.00,
      invoiceMonth: '2024-07',
      generatedAt: '2024-08-15T10:30:00Z',
      vesselName: 'Sapphire Voyager',
      origin: 'Gladstone, Australia',
      destination: 'Vizag Port, India',
      coalType: 'Thermal Coal - 6000 GAR'
    },
    {
      id: 'AR-2024-012',
      client: 'RNR Steels and Metals',
      shipmentRef: 'SHP-2024-058',
      description: 'Coal Trading Management Services - June 2024',
      invoiceDate: '2024-07-15',
      dueDate: '2024-08-15',
      tonnage: 395000,
      feeRate: 3.5,
      amountUSD: 1382500.00,
      amountAUD: 2101400.00,
      amountINR: 115438750.00,
      exchangeRate: 83.50,
      audExchangeRate: 1.52,
      currency: 'USD',
      status: 'paid',
      approvalStatus: 'approved',
      feeType: 'Management Fee',
      paymentTerms: 'Net 30',
      paidDate: '2024-08-12',
      paidAmount: 1382500.00,
      invoiceMonth: '2024-06',
      generatedAt: '2024-07-15T10:30:00Z',
      vesselName: 'Ruby Navigator',
      origin: 'Dalrymple Bay, Australia',
      destination: 'Kandla Port, India',
      coalType: 'Thermal Coal - 5800 GAR'
    }
  ]);

  // Enhanced mock shipment data with realistic coal trading volumes
  const mockShipments = [
    { 
      id: 1, 
      vesselName: "Pacific Glory", 
      supplier: "Whitehaven Coal", 
      buyer: "Adani Power Limited", 
      tonnage: 320000, 
      status: "loading", 
      etd: "2024-12-18", 
      eta: "2025-01-05",
      origin: "Newcastle, Australia",
      destination: "Mundra Port, India",
      coalType: "Thermal Coal - 5500 GAR",
      shipmentValue: 32000000
    },
    { 
      id: 2, 
      vesselName: "Ocean Harmony", 
      supplier: "Yancoal Australia", 
      buyer: "NTPC Limited", 
      tonnage: 150000, 
      status: "in-transit", 
      etd: "2024-12-22", 
      eta: "2025-01-08",
      origin: "Gladstone, Australia",
      destination: "Vizag Port, India",
      coalType: "Thermal Coal - 6000 GAR",
      shipmentValue: 15000000
    },
    { 
      id: 3, 
      vesselName: "Star Navigator", 
      supplier: "Glencore Coal Assets", 
      buyer: "Tata Power Company", 
      tonnage: 380000, 
      status: "contract-signed", 
      etd: "2025-01-08", 
      eta: "2025-01-25",
      origin: "Newcastle, Australia",
      destination: "Kandla Port, India",
      coalType: "Thermal Coal - 5800 GAR",
      shipmentValue: 38000000
    },
    { 
      id: 4, 
      vesselName: "Maritime Express", 
      supplier: "Peabody Energy Australia", 
      buyer: "JSW Energy Limited", 
      tonnage: 265000, 
      status: "lc-received", 
      etd: "2025-01-12", 
      eta: "2025-01-28",
      origin: "Dalrymple Bay, Australia",
      destination: "Paradip Port, India",
      coalType: "Thermal Coal - 5500 GAR",
      shipmentValue: 26500000
    },
    { 
      id: 5, 
      vesselName: "Global Trader", 
      supplier: "Whitehaven Coal", 
      buyer: "Adani Power Limited", 
      tonnage: 185000, 
      status: "contract-signed", 
      etd: "2025-01-18", 
      eta: "2025-02-03",
      origin: "Hay Point, Australia",
      destination: "Tuticorin Port, India",
      coalType: "Thermal Coal - 6200 GAR",
      shipmentValue: 18500000
    },
    { 
      id: 6, 
      vesselName: "Horizon Voyager", 
      supplier: "Yancoal Australia", 
      buyer: "NTPC Limited", 
      tonnage: 340000, 
      status: "discharged", 
      etd: "2024-11-22", 
      eta: "2024-12-08",
      origin: "Brisbane, Australia",
      destination: "Chennai Port, India",
      coalType: "Thermal Coal - 5700 GAR",
      shipmentValue: 34000000
    },
    { 
      id: 7, 
      vesselName: "Neptune Star", 
      supplier: "Glencore Coal Assets", 
      buyer: "Tata Power Company", 
      tonnage: 225000, 
      status: "completed", 
      etd: "2024-11-12", 
      eta: "2024-11-28",
      origin: "Newcastle, Australia",
      destination: "Haldia Port, India",
      coalType: "Thermal Coal - 6000 GAR",
      shipmentValue: 22500000
    },
    { 
      id: 8, 
      vesselName: "Coastal Pride", 
      supplier: "Peabody Energy Australia", 
      buyer: "JSW Energy Limited", 
      tonnage: 420000, 
      status: "contract-signed", 
      etd: "2025-01-22", 
      eta: "2025-02-07",
      origin: "Port Kembla, Australia",
      destination: "Krishnapatnam Port, India",
      coalType: "Thermal Coal - 5500 GAR",
      shipmentValue: 42000000
    }
  ];

  const handleGenerateInvoice = () => {
    setShowGenerateInvoiceModal(true);
  };

  const handleSaveGeneratedInvoice = (invoiceData) => {
    console.log('Generating invoice:', invoiceData);
    // TODO: Implement invoice generation
    alert('✅ Invoice generated and sent successfully!');
    setShowGenerateInvoiceModal(false);
  };

  const handleSendReminder = (invoiceId) => {
    console.log('Sending reminder for:', invoiceId);
    // TODO: Implement reminder sending
    alert('✅ Payment reminder sent!');
  };

  const handleRecordPayment = (invoiceId, amount) => {
    console.log('Recording payment:', invoiceId, amount);
    // TODO: Implement payment recording
    alert(`✅ Payment of ${amount} recorded!`);
  };

  const handleWriteOff = (invoiceId) => {
    if (confirm('Are you sure you want to write off this invoice?')) {
      console.log('Writing off invoice:', invoiceId);
      // TODO: Implement write-off
      alert('✅ Invoice written off!');
    }
  };

  const handleEdit = (invoice) => {
    console.log('Editing invoice:', invoice);
    // When editing rejected invoice, change status to pending
    const updatedStatus = invoice?.status === 'pending' ? 'pending' : 'pending';
    setReceivables(prevReceivables => 
      prevReceivables?.map(r => 
        r?.id === invoice?.id ? { ...r, status: updatedStatus } : r
      )
    );
    setIsInvoiceViewOpen(false);
    alert(`Editing invoice ${invoice?.id}. Status changed to: ${updatedStatus}`);
  };

  const handleApprove = (invoice) => {
    console.log('Approving invoice:', invoice);
    // Update invoice status to approved (payment status)
    setReceivables(prevReceivables => 
      prevReceivables?.map(r => 
        r?.id === invoice?.id ? { ...r, status: 'approved', approvalStatus: 'approved' } : r
      )
    );
    setIsInvoiceViewOpen(false);
    alert(`✅ Invoice ${invoice?.id} approved successfully!`);
  };

  const handleReject = (invoice) => {
    if (!window.confirm(`Are you sure you want to reject invoice ${invoice?.id}?`)) {
      return;
    }
    console.log('Rejecting invoice:', invoice);
    // When rejecting, change status to pending
    setReceivables(prevReceivables => 
      prevReceivables?.map(r => 
        r?.id === invoice?.id ? { ...r, status: 'pending', approvalStatus: 'rejected' } : r
      )
    );
    setIsInvoiceViewOpen(false);
    alert(`Invoice ${invoice?.id} rejected and moved to Pending status.`);
  };

  const handleSend = (invoice) => {
    console.log('Sending invoice:', invoice);
    // TODO: Implement invoice sending functionality
    alert(`✅ Invoice ${invoice?.id} sent successfully to ${invoice?.client}!`);
  };

  const totalReceivable = receivables?.filter(r => r?.status !== 'paid')?.reduce((sum, r) => sum + r?.amountUSD, 0);

  const overdueAmount = receivables?.filter(r => r?.status === 'overdue')?.reduce((sum, r) => sum + r?.amountUSD, 0);

  const receivedThisMonth = receivables?.filter(r => r?.status === 'paid' && new Date(r.paidDate)?.getMonth() === new Date()?.getMonth())?.reduce((sum, r) => sum + r?.paidAmount, 0);

  const avgCollectionTime = 25; // days

  const filteredReceivables = receivables?.filter(receivable => {
    if (selectedStatus !== 'all' && receivable?.status !== selectedStatus) return false;
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      return (receivable?.id?.toLowerCase()?.includes(searchLower) ||
      receivable?.shipmentRef?.toLowerCase()?.includes(searchLower) || receivable?.client?.toLowerCase()?.includes(searchLower));
    }
    return true;
  });

  const handleExport = () => {
    alert(`✅ Exporting ${filteredReceivables?.length} receivables...`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      overdue: 'bg-red-100 text-red-800',
      paid: 'bg-green-100 text-green-800',
    };
    return styles?.[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden mt-[65px]">
        {/* Mobile-optimized header */}
        <header className="bg-card border-b border-border px-3 sm:px-4 md:px-6 py-3 sm:py-4 sticky top-[65px] z-20">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground truncate">
                  Accounts Receivable
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">
                  Customer invoice and payment management
                </p>
              </div>
              
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleGenerateInvoice}
                  className="text-xs sm:text-sm px-3 sm:px-4 py-2 min-h-[44px] sm:min-h-auto flex-1 sm:flex-initial"
                >
                  <span className="hidden sm:inline">Generate Invoice</span>
                  <span className="sm:hidden">New Invoice</span>
                </Button>
              </div>
            </div>

            {/* Quick stats - responsive grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600 rounded-lg p-2">
                    <DollarSign className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700 font-medium">Total Receivable</p>
                    <p className="text-lg font-bold text-blue-900">
                      ${totalReceivable?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 border border-red-200">
                <div className="flex items-center gap-3">
                  <div className="bg-red-600 rounded-lg p-2">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-red-700 font-medium">Overdue</p>
                    <p className="text-lg font-bold text-red-900">
                      ${overdueAmount?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="bg-green-600 rounded-lg p-2">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700 font-medium">Received This Month</p>
                    <p className="text-lg font-bold text-green-900">
                      ${receivedThisMonth?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-600 rounded-lg p-2">
                    <TrendingUp className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-700 font-medium">Avg Collection Time</p>
                    <p className="text-lg font-bold text-purple-900">{avgCollectionTime} days</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <div className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
          {/* Search and filters */}
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by ID, Client, or Shipment Ref..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e?.target?.value)}
              className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
            </select>
            <Button
              variant="outline"
              onClick={handleExport}
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto -mx-3 sm:mx-0 bg-card rounded-lg border border-border">
            <table className="w-full min-w-[800px]">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Invoice ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Shipment Ref
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Vessel
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Tonnage
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Amount (USD)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredReceivables?.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-muted-foreground">
                      No receivables found
                    </td>
                  </tr>
                ) : (
                  filteredReceivables?.map((receivable) => (
                    <tr key={receivable?.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        {receivable?.id}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {receivable?.client}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {receivable?.shipmentRef}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {receivable?.vesselName}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {receivable?.tonnage?.toLocaleString()} MT
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground">
                        ${receivable?.amountUSD?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">
                        {new Date(receivable?.dueDate)?.toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(receivable?.status)}`}>
                          {receivable?.status?.charAt(0)?.toUpperCase() + receivable?.status?.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          {receivable?.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleSendReminder(receivable?.id)}
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                title="Send Reminder"
                              >
                                <Icon name="Bell" size={18} />
                              </button>
                              <button
                                onClick={() => handleRecordPayment(receivable?.id, receivable?.amountUSD)}
                                className="text-green-600 hover:text-green-800 transition-colors"
                                title="Record Payment"
                              >
                                <Icon name="CheckCircle" size={18} />
                              </button>
                            </>
                          )}
                          {receivable?.status === 'overdue' && (
                            <>
                              <button
                                onClick={() => handleSendReminder(receivable?.id)}
                                className="text-orange-600 hover:text-orange-800 transition-colors"
                                title="Send Reminder"
                              >
                                <Icon name="AlertCircle" size={18} />
                              </button>
                              <button
                                onClick={() => handleWriteOff(receivable?.id)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Write Off"
                              >
                                <Icon name="XCircle" size={18} />
                              </button>
                            </>
                          )}
                          {receivable?.status === 'approved' && (
                            <button
                              onClick={() => {
                                setSelectedInvoice(receivable);
                                setIsInvoiceViewOpen(true);
                              }}
                              className="text-purple-600 hover:text-purple-800 transition-colors"
                              title="Send Invoice"
                            >
                              <Icon name="Send" size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedInvoice(receivable);
                              setIsInvoiceViewOpen(true);
                            }}
                            className="text-gray-600 hover:text-gray-800 transition-colors"
                            title="View Details"
                          >
                            <Icon name="Eye" size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add InvoiceTemplate Modal */}
        {selectedInvoice && (
          <InvoiceTemplate
            invoice={selectedInvoice}
            isOpen={isInvoiceViewOpen}
            onClose={() => {
              setIsInvoiceViewOpen(false);
              setSelectedInvoice(null);
            }}
            onEdit={handleEdit}
            onApprove={handleApprove}
            onReject={handleReject}
            onSend={handleSend}
          />
        )}

        {/* Add GenerateInvoiceModal */}
        {showGenerateInvoiceModal && (
          <GenerateInvoiceModal
            isOpen={showGenerateInvoiceModal}
            onClose={() => setShowGenerateInvoiceModal(false)}
            onSave={handleSaveGeneratedInvoice}
            onGenerate={handleSaveGeneratedInvoice}
            existingInvoices={receivables}
            shipments={mockShipments}
          />
        )}
      </div>
    </div>
  );
};

export default AccountsReceivable;